import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  TableCell,
  TableRow,
  TableHead,
  TableHeader,
  TableBody,
  Table,
} from "@/components/ui/table";
import {
  Loader2,
  RefreshCw,
  Clock,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { TaskLogEntry, TaskLogsResponse } from "@shared/api-types";
import { fetchTaskLogs } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";

// Custom hook for managing intervals with cleanup
function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef<() => void>();

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval
  useEffect(() => {
    function tick() {
      savedCallback.current?.();
    }

    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

// Helper function to format timestamps with error handling
const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "Invalid date";
    }

    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).format(date);
  } catch (error) {
    console.error("Error formatting date:", error, dateString);
    return "Error";
  }
};

// Helper function to get a human-readable task name
// Just use the name field directly without processing as requested
const getTaskDisplayName = (
  name: string | undefined,
  rowKey: string,
): string => {
  // Handle undefined inputs with fallbacks
  if (!name) return rowKey;
  return name + ": " + rowKey;
};

// Status badge component
const StatusBadge = ({ status }: { status: TaskLogEntry["status"] }) => {
  switch (status) {
    case "pending":
      return (
        <Badge
          variant="outline"
          className="bg-yellow-50 text-yellow-700 border-yellow-200"
        >
          <Clock className="w-3 h-3 mr-1" /> Pending
        </Badge>
      );
    case "running":
      return (
        <Badge
          variant="outline"
          className="bg-blue-50 text-blue-700 border-blue-200"
        >
          <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Running
        </Badge>
      );
    case "completed":
      return (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-700 border-green-200"
        >
          <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
        </Badge>
      );
    case "error":
      return (
        <Badge
          variant="outline"
          className="bg-red-50 text-red-700 border-red-200"
        >
          <AlertCircle className="w-3 h-3 mr-1" /> Failed
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

export default function TaskLogsPage() {
  const { toast } = useToast();
  const [selectedLog, setSelectedLog] = useState<TaskLogEntry | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [configError, setConfigError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [refreshInterval, setRefreshInterval] = useState<number>(20); // Seconds

  // Handle refresh operations
  const handleRefresh = () => {
    if (!isRefetching) {
      refetch();
    }
  };

  // Setup auto-refresh with the useInterval hook
  useInterval(
    () => {
      if (autoRefresh && !isRefetching) {
        console.log(`Auto-refreshing task logs (${refreshInterval}s interval)`);
        refetch();
      }
    },
    autoRefresh ? refreshInterval * 1000 : null,
  );

  // Toggle auto-refresh
  const toggleAutoRefresh = () => {
    setAutoRefresh((prev) => !prev);
    if (!autoRefresh) {
      toast({
        title: "Auto-refresh enabled",
        description: `Task logs will refresh every ${refreshInterval} seconds`,
      });
    } else {
      toast({
        title: "Auto-refresh disabled",
        description: "Manual refresh only",
      });
    }
  };

  // Fetch task logs using TanStack Query
  const {
    data: logsResponse,
    isLoading,
    refetch,
    isRefetching,
    error,
  } = useQuery<TaskLogsResponse>({
    queryKey: ["/api/tasks/logs", filterStatus], // Ensure filterStatus is part of the queryKey to trigger refetch
    queryFn: async () => {
      try {
        const response = await fetchTaskLogs({
          status: filterStatus === "all" ? undefined : filterStatus,
        });

        // If we have a configuration error, store it for display
        if (!response.success && response.message?.includes("not configured")) {
          setConfigError(response.message);
        } else {
          setConfigError(null);
        }

        return response;
      } catch (err) {
        console.error("Error fetching task logs:", err);
        return {
          success: false,
          logs: [],
          statusCounts: {
            pending: 0,
            running: 0,
            completed: 0,
            failed: 0,
            total: 0,
          },
          error: (err as Error).message || "Unknown error",
        };
      }
    },
    retry: false,
  });

  // Access logs safely with fallback to empty array and sort by createdOn date (newest first)
  const logs = (logsResponse?.logs || []).sort((a, b) => {
    // Handle missing createdOn dates
    if (!a.createdOn) return 1;
    if (!b.createdOn) return -1;
    
    // Sort newest first (descending order)
    return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();
  });
  
  const statusCounts = logsResponse?.statusCounts || {
    pending: 0,
    running: 0,
    completed: 0,
    failed: 0,
    total: 0,
  };

  // Open log details dialog
  const openLogDetails = async (log: TaskLogEntry) => {
    setSelectedLog(log);

  };

  return (
    <div className="container py-6 px-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Task Logs</h1>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <Switch
                id="auto-refresh"
                checked={autoRefresh}
                onCheckedChange={toggleAutoRefresh}
              />
              <label
                htmlFor="auto-refresh"
                className="text-sm cursor-pointer flex items-center"
              >
                {autoRefresh ? (
                  <>
                    <Play className="h-3 w-3 mr-1 text-green-600" />{" "}
                    Auto-refresh
                  </>
                ) : (
                  <>
                    <Pause className="h-3 w-3 mr-1 text-gray-500" />{" "}
                    Auto-refresh
                  </>
                )}
              </label>
            </div>
            {autoRefresh && (
              <div className="flex items-center gap-1">
                <Select
                  value={refreshInterval.toString()}
                  onValueChange={(value) => {
                    const newInterval = parseInt(value);
                    setRefreshInterval(newInterval);
                    if (autoRefresh) {
                      toast({
                        title: "Refresh interval updated",
                        description: `Task logs will refresh every ${newInterval} seconds`,
                      });
                    }
                  }}
                >
                  <SelectTrigger className="h-7 w-16 text-xs">
                    <SelectValue placeholder="20s" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5s</SelectItem>
                    <SelectItem value="10">10s</SelectItem>
                    <SelectItem value="20">20s</SelectItem>
                    <SelectItem value="30">30s</SelectItem>
                    <SelectItem value="60">60s</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">interval</span>
              </div>
            )}
          </div>
          <Button onClick={handleRefresh} disabled={isRefetching}>
            {isRefetching ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {configError && (
        <Card className="mb-6 border-yellow-300 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-yellow-800 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Configuration Warning
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-yellow-800">{configError}</p>
            <p className="text-yellow-700 mt-2 text-sm">
              Please ensure that AZURE_STORAGE_ACCOUNT_NAME,
              AZURE_STORAGE_ACCOUNT_KEY, and AZURE_STORAGE_MANAGEMENT_TABLE_NAME
              environment variables are set correctly.
            </p>
          </CardContent>
        </Card>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Task Execution History</CardTitle>
          <CardDescription>
            View the execution history and status of system management tasks
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-4 justify-between">
            <div className="flex gap-4">
              <div className="w-48">
                <label className="text-sm font-medium mb-1 block">
                  Status Filter
                </label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="running">Running</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task Name</TableHead>
                    <TableHead>Records</TableHead>
                    <TableHead>Processed %</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created On</TableHead>
                    <TableHead>Completed On</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center py-6 text-muted-foreground"
                      >
                        No task logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage).map((log: TaskLogEntry) => (
                      <TableRow key={log.rowKey}>
                        <TableCell className="font-medium">
                          {getTaskDisplayName(log.name, log.rowKey)}
                        </TableCell>
                        <TableCell>
                          {log.recordLimit ? `${log.processedRecords}/${log.recordLimit}` : `${log.processedRecords} (${log.totalRecords})`}
                        </TableCell>
                        <TableCell>
                          {log.processedPercentage !== undefined
                            ? `${parseFloat(String(log.processedPercentage)).toFixed(2)}%`
                            : '-'
                          }
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={log.status} />
                        </TableCell>
                        <TableCell>{formatDate(log.createdOn)}</TableCell>
                        <TableCell>{formatDate(log.completedOn)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openLogDetails(log)}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-4 text-sm text-muted-foreground">
            <div>
              {logs.length > 0 ? (
                <>
                  Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, logs.length)} of {logs.length} items
                </>
              ) : (
                'No items to display'
              )}
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1 || logs.length === 0}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1 || logs.length === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <span className="px-2">
                  Page {currentPage} of {Math.max(1, Math.ceil(logs.length / rowsPerPage))}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(logs.length / rowsPerPage)))}
                  disabled={currentPage >= Math.ceil(logs.length / rowsPerPage) || logs.length === 0}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.ceil(logs.length / rowsPerPage))}
                  disabled={currentPage >= Math.ceil(logs.length / rowsPerPage) || logs.length === 0}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Rows:</span>
                <Select
                  value={rowsPerPage.toString()}
                  onValueChange={(value) => {
                    setRowsPerPage(parseInt(value));
                    setCurrentPage(1); // Reset to first page when changing rows per page
                  }}
                >
                  <SelectTrigger className="h-8 w-[70px]">
                    <SelectValue placeholder="10" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Log Details Dialog */}
      <Dialog
        open={!!selectedLog}
        onOpenChange={(open) => !open && setSelectedLog(null)}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Task Log Details</DialogTitle>
            <DialogDescription>
              Detailed information about task execution
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-sm font-medium">Task</h3>
                  <p>
                    {getTaskDisplayName(
                      selectedLog.name || selectedLog.name,
                      selectedLog.rowKey,
                    )}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium">Status</h3>
                  <StatusBadge status={selectedLog.status} />
                </div>
                <div>
                  <h3 className="text-sm font-medium">Started At</h3>
                  <p>{formatDate(selectedLog.createdOn)}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium">Completed At</h3>
                  <p>
                    {selectedLog.completedOn
                      ? formatDate(selectedLog.completedOn)
                      : "Not completed"}
                  </p>
                </div>

                {selectedLog.processedPercentage !== undefined && (
                  <div>
                    <h3 className="text-sm font-medium">Progress</h3>
                    <p>{parseFloat(String(selectedLog.processedPercentage)).toFixed(2)}%</p>
                  </div>
                )}
                {selectedLog.totalRecords !== undefined && (
                  <div>
                    <h3 className="text-sm font-medium">Total Items</h3>
                    <p>{selectedLog.totalRecords}</p>
                  </div>
                )}

                {selectedLog.response && (
                  <div>
                    <h3 className="text-sm font-medium">Response</h3>
                    <div className="bg-muted p-3 rounded-md whitespace-pre-wrap max-h-60 overflow-y-auto text-sm">
                      {typeof selectedLog.response === "string"
                        ? JSON.stringify(
                            JSON.parse(selectedLog.response),
                            null,
                            2,
                          )
                        : JSON.stringify(selectedLog.response, null, 2)}
                    </div>
                  </div>
                )}
                {selectedLog.results && (
                  <div>
                    <h3 className="text-sm font-medium">Results</h3>
                    <div className="bg-muted p-3 rounded-md whitespace-pre-wrap max-h-60 overflow-y-auto text-sm">
                      {typeof selectedLog.results === "string"
                        ? JSON.stringify(
                            JSON.parse(selectedLog.results),
                            null,
                            2,
                          )
                        : JSON.stringify(selectedLog.results, null, 2)}
                    </div>
                  </div>
                )}
              </div>
              {selectedLog.errorMessage && (
                <div>
                  <h3 className="text-sm font-medium mb-2 text-red-600">
                    Error
                  </h3>
                  <div className="bg-red-50 border border-red-100 p-3 rounded-md whitespace-pre-wrap max-h-60 overflow-y-auto text-sm text-red-700">
                    {selectedLog.errorMessage}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
